import { ARTICLE_TAG_COLORS } from '@application/constants';
import { useRouter } from '@application/utils';
import type { ArticleGetArticleQuery } from '@gql/graphql';
import type { ArticlePageData, ArticlePageProps } from '@presentation/pages';
import { useMemo } from 'react';
import { FaCheck } from 'react-icons/fa';

export const useFormatArticle = (
  article?: NonNullable<ArticleGetArticleQuery['article']>[number] | null,
) => {
  const { translate, navigateToRoute, languageCode } = useRouter();

  const translation = useMemo(() => {
    if (!article) return;
    return article.translations.find((t) => t.languageCode === languageCode);
  }, [article, languageCode]);

  const formattedArticle: Partial<ArticlePageProps> = useMemo(() => {
    if (!article || !translation) return {};

    return {
      header: {
        title: translation.title,
        tags: [
          ...(article.reviewed
            ? [
                {
                  theme: 'light',
                  bgColor: 'white',
                  content: (
                    <>
                      <FaCheck /> {translate('glossary.reviewedArticle')}
                    </>
                  ),
                },
              ]
            : []),
          ...(article.tags ?? []).map((tag) => ({
            content: translate(`common.articleTags.${tag}`),
            bgColor: ARTICLE_TAG_COLORS[tag],
            onClick: () =>
              navigateToRoute('articleList', {
                search: {
                  tags: tag,
                },
              }),
          })),
        ],
      },
      contentHtml: translation.content,
    } as Partial<ArticlePageData>;
  }, [article, navigateToRoute, translate, translation]);

  return { formattedArticle };
};
